import React from "react";
import Avatar from "../../../components/common/Avatar";
import { dayName, isWeekend, toISODate } from "../../../utils/weekDates";
import { formatDays } from "../../../utils/leaveDays";
import { CLASH_THRESHOLD, calendarBlocks, monthDays, offCounts, typeColor } from "./leaveConfig";
import styles from "./LeaveUI.module.css";

// One row per person for the month: leave as blocks (pending dashed), and a People off row that goes red on clash days.
export default function LeaveCalendar({ month, people, requests, types, onOpen }) {
  const days = monthDays(month);
  const today = toISODate(new Date());
  const counts = offCounts(requests, days);
  const blocks = calendarBlocks(requests, days);
  const typeById = Object.fromEntries(types.map((t) => [Number(t.leave_type_id ?? t.time_off_type_id), t]));

  const track = (children) => (
    <div className={styles.calTrack} style={{ gridTemplateColumns: `repeat(${days.length}, minmax(24px, 1fr))` }}>{children}</div>
  );
  const dayCell = (d, i, content = null, extra = "") => (
    <div
      key={d}
      className={`${styles.calCell} ${isWeekend(d) ? styles.calWeekend : ""} ${d === today ? styles.calToday : ""} ${extra}`}
      style={{ gridColumn: i + 1, gridRow: 1 }}
    >
      {content}
    </div>
  );

  return (
    <div className={styles.calWrap}>
      <div className={styles.calRow}>
        <div className={`${styles.calName} ${styles.calHead}`}>Employee</div>
        {track(days.map((d, i) => dayCell(d, i, (
          <span className={styles.calDayHead}>
            <small>{dayName(d, "narrow")}</small>
            {Number(d.slice(8))}
          </span>
        ))))}
      </div>

      {people.map((person) => (
        <div key={person.id} className={styles.calRow}>
          <div className={styles.calName}>
            <Avatar name={person.name} imageUrl={person.avatar} size="sm" round />
            <span>{person.name.split(" ")[0]}</span>
          </div>
          {track([
            ...days.map((d, i) => dayCell(d, i)),
            ...blocks.filter((b) => b.request.employee_id === person.id).map(({ request, from, to }) => {
              const type = typeById[request.type_id];
              const pending = request.status === "pending";
              const color = typeColor(type, types);
              return (
                <button
                  key={request.id}
                  type="button"
                  className={`${styles.calBlock} ${pending ? styles.calPending : ""}`}
                  style={{ gridColumn: `${from + 1} / ${to + 2}`, gridRow: 1, "--block": color }}
                  title={`${request.employee_name} · ${request.type_name} · ${formatDays(request.days)} day${request.days === 1 ? "" : "s"}${pending ? " · pending" : ""}`}
                  aria-label={`${request.employee_name}, ${request.type_name}, ${request.statusLabel}`}
                  onClick={() => onOpen(request)}
                >
                  {request.days < 1 ? "½" : ""}
                </button>
              );
            }),
          ])}
        </div>
      ))}

      <div className={`${styles.calRow} ${styles.calTotals}`}>
        <div className={styles.calName}>People off</div>
        {track(days.map((d, i) => dayCell(d, i, counts[i] || "", counts[i] >= CLASH_THRESHOLD ? styles.calClash : "")))}
      </div>
      <p className={styles.calNote}>Days with {CLASH_THRESHOLD} or more people off are marked red. Click a block to open the request.</p>
    </div>
  );
}
